import React, { useState } from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Mail, MessageSquare, CalendarDays, Globe, Send, Phone, Link as LinkIcon } from "lucide-react";
import { ContactLink, SupportLink } from "@/pages/AdminTranslators";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";


interface Translator {
  id: string;
  name: string;
  isActive: boolean;
  slug: string;
  avatar?: string;
  downloads?: number;
  uploads?: number;
  gamesTranslated?: number;
  description?: string;
  registrationDate?: string;
  lastLogin?: string;
  supportLinks?: SupportLink[];
  contactLinks?: ContactLink[];
}

interface TranslatorCardProps {
  translator: Translator;
}

const TranslatorCard: React.FC<TranslatorCardProps> = ({ translator }) => {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const needsTruncation = translator.description && translator.description.length > 150;

  const getContactLinkIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'email': return <Mail size={18} />;
      case 'discord': return <MessageSquare size={18} />;
      case 'weboldal': return <Globe size={18} />;
      case 'telegram': return <Send size={18} />;
      case 'telefon': return <Phone size={18} />;
      default: return <LinkIcon size={18} />;
    }
  };

  const getSupportLinkIcon = (type: SupportLink['type']) => {

    switch (type) {
      case 'paypal': return <i className="bi bi-paypal"></i>;
      case 'buymeacoffee': return <i className="bi bi-cup-hot"></i>; // nincs buymeacoffee, kávés ikon helyette
      case 'crypto': return <i className="bi bi-currency-bitcoin"></i>;
      case 'bank': return <i className="bi bi-bank"></i>;
      case 'patreon': return <i className="bi bi-heart"></i>; // nincs patreon, szív ikon helyette
      case 'steam': return <i className="bi bi-steam"></i>;
      case 'revolut': return <i className="bi bi-credit-card"></i>; // nincs revolut, kártya ikon helyette
      case 'ko-fi': return <i className="bi bi-cup-hot"></i>; // nincs buymeacoffee, kávés ikon helyette
      case 'discord': return <i className="bi bi-discord"></i>;
      case 'other': return <i className="bi bi-credit-card-2-front"></i>;
      default: return null;
    }

  };

  const getSupportLinkDisplayName = (link: SupportLink) => {
    if (link.type === 'other' && link.label) {
      return link.label;
    }
    switch (link.type) {
      case 'paypal': return 'PayPal';
      case 'buymeacoffee': return 'Buy Me a Coffee';
      case 'crypto': return 'Kripto';
      case 'bank': return 'Banki átutalás';
      case 'patreon': return 'Patreon';
      case 'steam': return 'Steam';
      case 'revolut': return 'Revolut';
      case 'discord': return 'Discord';
      case 'ko-fi': return 'Ko-fi';
      default: return 'Támogatás';
    }
  };

  return (
    <TooltipProvider>
      <Card className="bg-secondary text-white border-border shadow-lg hover:shadow-primary/30 transition-shadow duration-300 flex flex-col h-full">
        <CardHeader className="p-4 bg-card/50 rounded-t-lg">
          <div className="flex items-start justify-between gap-4">
            <Link to={`/translator/${translator.slug}`} className="flex items-center gap-4 group">
              <Avatar className="w-16 h-16 border-2 border-primary group-hover:border-accent transition-colors">
                <AvatarImage src={translator.avatar} alt={translator.name} className="object-cover" />
                <AvatarFallback className="bg-primary text-primary-foreground text-xl">{translator.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-2xl font-bold text-white group-hover:text-primary transition-colors">{translator.name}</CardTitle>
                <div className="text-xs text-white space-y-1 mt-1">
                  <div className="flex items-center gap-2"><CalendarDays size={14} /> Csatlakozott: {translator.registrationDate}</div>
                  <div className="flex items-center gap-2"><CalendarDays size={14} /> Utoljára aktív: {translator.lastLogin}</div>
                </div>
              </div>
            </Link>

          </div>
        </CardHeader>

        <CardContent className="p-4 flex-grow space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center text-sm border-y border-border py-2">
            <div>
              <div className="font-bold text-lg text-white">{translator.gamesTranslated || 0}</div>
              <div className="text-white text-xs">Játék</div>
            </div>
            <div>
              <div className="font-bold text-lg text-white">{translator.downloads?.toLocaleString() || 0}</div>
              <div className="text-white text-xs">Letöltés</div>
            </div>
            <div>
              <div className="font-bold text-lg text-white">{translator.uploads || 0}</div>
              <div className="text-white text-xs">Feltöltés</div>
            </div>
          </div>

          {translator.description && (
            <div>
              <div
                className={cn('text-sm text-white prose prose-sm prose-invert max-w-none', !showFullDescription && needsTruncation && 'line-clamp-4')}
                dangerouslySetInnerHTML={{ __html: translator.description }}
              />
              {needsTruncation && (
                <Button
                  variant="link"
                  onClick={toggleDescription}
                  className="p-0 h-auto text-primary hover:text-primary-foreground transition-colors duration-200"
                >
                  {showFullDescription ? "Kevesebb" : "Többet..."}
                </Button>
              )}
            </div>
          )}
        </CardContent>
        {((translator.contactLinks && translator.contactLinks.length > 0) || (translator.supportLinks && translator.supportLinks.length > 0)) && (

          <CardFooter className="p-4 pt-4 border-t border-border flex flex-col gap-4 mt-auto">
            <div className="w-full">
              <h3 className="text-base font-semibold mb-2 text-white">Elérhetőségek</h3>
              {translator.contactLinks && translator.contactLinks.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {translator.contactLinks.map((link, index) => (
                    <Tooltip key={index}>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="icon" className="bg-muted text-white border-border h-9 w-9" asChild>
                          <a href={link.value.startsWith('http') ? link.value : `mailto:${link.value}`} target="_blank" rel="noopener noreferrer">
                            {getContactLinkIcon(link.type)}
                          </a>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{link.type}</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">Nincs megadva.</p>
              )}
            </div>

            {translator.isActive && (
              <div className="w-full p-3 rounded-lg bg-primary/10 border border-primary/20">
                <h3 className="text-base font-semibold mb-2 text-white">Támogatás</h3>
                {translator.supportLinks && translator.supportLinks.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {translator.supportLinks.map((link, index) => (
                      <Tooltip key={index}>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="icon" className="bg-muted text-white border-border h-9 w-9" asChild>
                            <a href={link.url} target="_blank" rel="noopener noreferrer">
                              {getSupportLinkIcon(link.type)}
                            </a>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{getSupportLinkDisplayName(link)}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">Nincs megadva.</p>
                )}
              </div>
            )}
          </CardFooter>

        )}
      </Card>
    </TooltipProvider>
  );
};

export default TranslatorCard;